import React, { useState, useEffect } from 'react';
import { Mail, Phone, ArrowLeft, Eye, EyeOff, Key, CheckCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import Button from '../../common/Button/Button';
import { validateForm, validateField, VALIDATION_RULES, ErrorMessage } from '../../../utils/formValidation';
import authService from '../../../api/services/authService';

const ForgotPassword = ({ onBackToLogin }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [deliveryMethod, setDeliveryMethod] = useState('email');
  const [formData, setFormData] = useState({
    emailOrPhone: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    let timer;
    if (currentStep === 2 && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }
    return () => clearInterval(timer);
  }, [currentStep, countdown]);

  const getValidationRules = (name) => {
    if (name === 'emailOrPhone') {
      return deliveryMethod === 'email'
        ? { ...VALIDATION_RULES.email, maxLength: 100, customLabel: 'Email' }
        : { ...VALIDATION_RULES.phone, customLabel: 'Phone number' };
    }
    if (name === 'otp') return VALIDATION_RULES.otp;
    if (name === 'newPassword') return VALIDATION_RULES.password;
    if (name === 'confirmPassword') return { required: true };
    return {};
  };

  const validateFieldLevel = (name, value) => {
    const rules = getValidationRules(name);
    let fieldError = '';

    if (name === 'confirmPassword') {
      if (!value) {
        fieldError = 'Please confirm your new password';
      } else if (value !== formData.newPassword) {
        fieldError = 'Passwords do not match';
      }
    } else {
      const fieldErrors = validateField(name, value, rules);
      if (fieldErrors.length > 0) {
        fieldError = fieldErrors[0];
      }
    }

    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));

    return !fieldError;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateFieldLevel(name, value);
  };

  // Mutations
  const sendOTPMutation = useMutation({
    mutationFn: (data) => authService.sendOTP(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message || 'OTP sent successfully!');
        setFormData(prev => ({ ...prev, otp: '' }));
        setCountdown(30);
        setCanResend(false);
        setCurrentStep(2);
      } else {
        toast.error(response.error || 'Failed to send OTP. Please try again.');
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to send OTP. Please try again.');
    }
  });

  const verifyOTPMutation = useMutation({
    mutationFn: (data) => authService.verifyOTP(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message || 'OTP verified successfully!');
        setCurrentStep(3);
      } else {
        toast.error(response.error || 'Invalid OTP. Please try again.');
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Invalid OTP. Please try again.');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (data) => authService.resetPassword(data),
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.message || 'Password reset successfully!');
        setCurrentStep(4);
      } else {
        toast.error(response.error || 'Failed to reset password. Please try again.');
      }
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to reset password. Please try again.');
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'emailOrPhone') {
      if (deliveryMethod === 'phone') {
        let phoneDigits = value.replace(/\D/g, '').slice(0, 10);
        if (phoneDigits.startsWith('0')) {
          phoneDigits = phoneDigits.substring(1);
        }
        setFormData(prev => ({ ...prev, [name]: phoneDigits }));
      } else {
        // For email, limit to 100 characters
        const emailValue = value.slice(0, 100);
        setFormData(prev => ({ ...prev, [name]: emailValue }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleDeliveryMethodChange = (method) => {
    setDeliveryMethod(method);
    setFormData(prev => ({ ...prev, emailOrPhone: '' }));
    setErrors({});
  };

  const validateCurrentStep = () => {
    if (currentStep === 1) {
      return validateFieldLevel('emailOrPhone', formData.emailOrPhone);
    } else if (currentStep === 2) {
      return validateFieldLevel('otp', formData.otp);
    } else if (currentStep === 3) {
      const isNewValid = validateFieldLevel('newPassword', formData.newPassword);
      const isConfirmValid = validateFieldLevel('confirmPassword', formData.confirmPassword);
      return isNewValid && isConfirmValid;
    }
    return true;
  };

  const handleSendOTP = (e) => {
    if (e) e.preventDefault();
    if (!validateCurrentStep()) return;
    sendOTPMutation.mutate({
      emailOrPhone: formData.emailOrPhone,
      method: deliveryMethod
    });
  };

  const handleResendOTP = () => {
    if (!validateFieldLevel('emailOrPhone', formData.emailOrPhone)) return;
    setErrors(prev => ({ ...prev, otp: '' }));
    sendOTPMutation.mutate({
      emailOrPhone: formData.emailOrPhone,
      method: deliveryMethod
    });
  };

  const handleVerifyOTP = (e) => {
    e.preventDefault();
    if (!validateCurrentStep()) return;
    verifyOTPMutation.mutate({
      emailOrPhone: formData.emailOrPhone,
      otp: formData.otp,
      method: deliveryMethod
    });
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    if (!validateCurrentStep()) return;
    resetPasswordMutation.mutate({
      emailOrPhone: formData.emailOrPhone,
      otp: formData.otp,
      newPassword: formData.newPassword,
      method: deliveryMethod
    });
  };


  const formatPhoneDisplay = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+91 ${cleaned.substring(0, 5)} ${cleaned.substring(5)}`;
    }
    return `+91 ${cleaned}`;
  };


  if (currentStep === 1) {
    return (
      <div className="w-full max-w-md mx-auto mt-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center mb-8 text-center">
              <div className="w-20 h-20 flex items-center justify-center overflow-hidden mb-4">
                <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="w-full h-full object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{process.env.REACT_APP_COMPANY_NAME}</h1>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Forgot Password?</h2>
            <p className="text-gray-600 text-sm">
              Don't worry! Enter your email or phone number and we'll send you a code to reset your password.
            </p>
          </div>



          <form onSubmit={handleSendOTP} className="space-y-4">
            <div>
              <label htmlFor="emailOrPhone" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  id="emailOrPhone"
                  name="emailOrPhone"
                  value={formData.emailOrPhone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  maxLength={100}
                  className={`w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent transition-colors ${errors.emailOrPhone ? 'border-red-500' : ''
                    }`}
                  placeholder="Enter your email address"
                />
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              loading={sendOTPMutation.isPending}
              disabled={sendOTPMutation.isPending}
            >
              {sendOTPMutation.isPending ? 'Sending...' : 'Send OTP'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="inline-flex items-center text-sm text-agrosaf-green hover:text-agrosaf-darkGreen font-medium transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </button>
          </div>
        </div >
      </div >
    );
  }

  if (currentStep === 2) {
    return (
      <div className="w-full max-w-md mx-auto mt-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center mb-8 text-center">
              <div className="w-24 h-24 flex items-center justify-center overflow-hidden mb-4">
                <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="w-full h-full object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{process.env.REACT_APP_COMPANY_NAME}</h1>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verify Code</h2>
            <p className="text-gray-600 text-sm">
              Code sent to your email
            </p>
            <p className="text-agrosaf-green font-medium text-sm mt-1">
              {formData.emailOrPhone}
            </p>
          </div>

          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-2">
                Enter 6-digit code
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  value={formData.otp}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent transition-colors text-center text-2xl tracking-widest ${errors.otp ? 'border-red-500' : ''
                    }`}
                  placeholder="• • • • • •"
                  maxLength="6"
                />
              </div>
              <ErrorMessage error={errors.otp} />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              loading={verifyOTPMutation.isPending}
              disabled={verifyOTPMutation.isPending}
            >
              {verifyOTPMutation.isPending ? 'Verifying...' : 'Verify'}
            </Button>
          </form>


          <div className="mt-4 text-center pb-2">
            <button
              type="button"
              onClick={() => {
                if (canResend && !sendOTPMutation.isPending) {
                  handleResendOTP();
                }
              }}
              disabled={!canResend || sendOTPMutation.isPending}
              className={`text-sm font-medium transition-colors ${canResend
                  ? 'text-agrosaf-green hover:text-agrosaf-darkGreen cursor-pointer'
                  : 'text-gray-400 cursor-not-allowed'
                }`}
            >
              {sendOTPMutation.isPending
                ? 'Sending...'
                : canResend
                  ? 'Resend Code'
                  : `Resend Code in ${countdown}s`
              }
            </button>
          </div>

          <div className="mt-2 text-center border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, otp: '' })); // Clear OTP field when going back
                setErrors({}); // Clear errors
                setCurrentStep(1);
              }}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Change email address
            </button>
          </div>
        </div >
      </div >
    );
  }

  if (currentStep === 3) {
    return (
      <div className="w-full max-w-md mx-auto mt-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center mb-8 text-center">
              <div className="w-24 h-24 flex items-center justify-center overflow-hidden mb-4">
                <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="w-full h-full object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{process.env.REACT_APP_COMPANY_NAME}</h1>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Create New Password</h2>
            <p className="text-gray-600 text-sm">
              Please create a strong password that you haven't used before
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent transition-colors ${errors.newPassword ? 'border-red-500' : ''
                    }`}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <ErrorMessage error={errors.newPassword} />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent transition-colors ${errors.confirmPassword ? 'border-red-500' : ''
                    }`}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <ErrorMessage error={errors.confirmPassword} />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              loading={resetPasswordMutation.isPending}
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </button>
          </div>
        </div >
      </div >
    );
  }

  if (currentStep === 4) {
    return (
      <div className="w-full max-w-md mx-auto mt-12">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex flex-col items-center mb-8 text-center">
              <div className="w-24 h-24 flex items-center justify-center overflow-hidden mb-4">
                <img src="/logo.png" alt={`${process.env.REACT_APP_COMPANY_NAME} Logo`} className="w-full h-full object-contain" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">{process.env.REACT_APP_COMPANY_NAME}</h1>
            </div>
            <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Reset Successfull</h2>
            <p className="text-gray-600 text-sm">
              Your password has been successfully reset. You can now login with your new password.
            </p>
          </div>

          <div className="space-y-4">
            <Button
              type="button"
              variant="primary"
              size="lg"
              className="w-full bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              onClick={onBackToLogin}
            >
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ForgotPassword;
